// @ts-nocheck
// @ts-ignore

import React from "react";

import Avatar from "@mui/material/Avatar";
import Grid from "@mui/material/Grid";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";

import ArrowForwardIosOutlinedIcon from "@mui/icons-material/ArrowForwardIosOutlined";

import { useNavigate } from "react-router-dom";

import momentTz from "moment-timezone";

import patientAvatar from "../avatar.png";

import { APPOINTMENT_STATUS } from "constants/constants";

const AppointmentListItem = ({ appointment }) => {
  const navigate = useNavigate();

  /* =====================================================
   * SAFETY
   * =================================================== */

  if (!appointment) {
    return null;
  }

  /* =====================================================
   * DISPLAY TIMEZONE
   *
   * slotTime remains the authoritative instant.
   * Timezone conversion here is display-only.
   * =================================================== */

  const patientTimeZone =
    localStorage.getItem("Time-Zone") || momentTz.tz.guess() || "UTC";

  /*
   * Both displayed date and time are now derived from
   * slotTime.
   *
   * Do not derive the displayed calendar date from the
   * DATEONLY appointment.date field because timezone
   * conversion can otherwise produce inconsistent results.
   */
  const slotMoment = momentTz.utc(appointment.slotTime).tz(patientTimeZone);

  const hasValidSlotTime = slotMoment.isValid();

  const convertedDate = hasValidSlotTime
    ? slotMoment.format("YYYY-MM-DD")
    : "—";

  const convertedTime = hasValidSlotTime ? slotMoment.format("hh:mm A") : "—";

  /* =====================================================
   * PROVIDER DISPLAY
   * =================================================== */

  const provider = appointment?.Doctor;

  const providerName =
    provider?.displayName ||
    [provider?.prefix, provider?.name].filter(Boolean).join(" ") ||
    provider?.doctorName ||
    "Unknown health professional";

  const providerProfession =
    provider?.professionType || provider?.profession || "Health professional";

  const providerImage = provider?.User?.profileImagePath || patientAvatar;

  /* =====================================================
   * ROUTING
   *
   * COMPLETED
   * → post-appointment details
   *
   * Other lifecycle states
   * → consultation context
   *
   * The consultation backend remains authoritative for:
   * PENDING / CANCELED / access-window state.
   * =================================================== */

  const handleOpenAppointment = () => {
    if (!appointment?.appointmentId) {
      return;
    }

    if (appointment.status === APPOINTMENT_STATUS.COMPLETED) {
      navigate(`/appointment-details/${appointment.appointmentId}`);

      return;
    }

    navigate(`/appointment-consultation/${appointment.appointmentId}`, {
      /*
       * Convenience only.
       *
       * AppointmentConsultation must independently
       * fetch authoritative consultation context.
       */
      state: {
        appointment,
      },
    });
  };

  /* =====================================================
   * KEYBOARD ACCESSIBILITY
   * =================================================== */

  const handleKeyDown = (event) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();

      handleOpenAppointment();
    }
  };

  /* =====================================================
   * STATUS
   * =================================================== */

  const status = appointment.status || "UNKNOWN";

  // const isOngoing = status === APPOINTMENT_STATUS.ONGOING;

  return (
    <div className="appointmentItemWrapper">
      {/*
       * Existing ONGOING visual retained only as a display
       * treatment.
       *
       * It does NOT authorize consultation access.
       */}
      {/* {isOngoing && <div aria-hidden="true" />} */}

    <Paper
  elevation={1}
  className={
    `appointmentData appointmentDataClickable ` +
    `appointmentCard-${status.toLowerCase()}`
  }
  role="button"
  tabIndex={0}
  aria-label={
    `View appointment details for ${providerName}`
  }
  onClick={handleOpenAppointment}
  onKeyDown={handleKeyDown}
>
  <Grid
  padding={1.5}
    container
    spacing={{
      xs: 1.5,
      sm: 2,
    }}
    className="calendarAppointmentGrid"
  >
    {/* =============================================
              APPOINTMENT ID
             ============================================= */}

          <Grid item xs={6} sm={3} md={2} lg={2}>
            <div className="appointmentIdColumn">
              <p className="appointmentFieldLabel">Appointment Id</p>

              <h6 className="appointmentIdValue">
                {appointment.appointmentId || ""}
              </h6>
            </div>
          </Grid>
          {/* =============================================
              PURPOSE
             ============================================= */}

          <Grid item xs={6} sm={3} md={2} lg={2}>
            <div className="appointmentPurposeColumn">
              <p className="appointmentFieldLabel">Purpose</p>

              <h6 className="appointmentPurposeValue">
                {appointment.purpose || "Consultation"}
              </h6>
            </div>
          </Grid>

          {/* =============================================
              PROVIDER
             ============================================= */}

          <Grid item xs={12} sm={6} md={3} lg={3}>
            <Stack
              direction="row"
              spacing={1.5}
              alignItems="center"
              className="appointmentProvider"
            >
              <Avatar
                alt={`${providerName} profile`}
                src={providerImage}
                className="appointmentProviderAvatar"
              />

              <div className="patientName3">
                <h6>{providerName}</h6>

                <p>{providerProfession}</p>
              </div>
            </Stack>
          </Grid>

          {/* =============================================
              DATE
             ============================================= */}

          <Grid item xs={6} sm={3} md={2} lg={1}>
            <div className="date2">
              <p>Date</p>

              <h6>{convertedDate}</h6>
            </div>
          </Grid>

          {/* =============================================
              TIME
             ============================================= */}

          <Grid item xs={6} sm={3} md={2} lg={1}>
            <div className="time2">
              <p>Time</p>

              <h6>{convertedTime}</h6>
            </div>
          </Grid>

          {/* =============================================
              STATUS
             ============================================= */}

          <Grid item xs={6} sm={3} md={2} lg={2}>
            <div className="appointmentStatusColumn">
              <p className="appointmentFieldLabel">Status</p>

              <span
                className={
                  `appointmentStatusBadge ` +
                  `appointmentStatus-${status.toLowerCase()}`
                }
              >
                {status}
              </span>
            </div>
          </Grid>

          {/* =============================================
              ACTION INDICATOR
             ============================================= */}

          <Grid item xs={6} sm={3} md={12} lg={1}>
            <div className="moreInfo2" aria-hidden="true">
              <span>More details</span>

              <ArrowForwardIosOutlinedIcon className="appointmentMoreIcon" />
            </div>
          </Grid>
        </Grid>
      </Paper>
    </div>
  );
};

export { AppointmentListItem };
